import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Terminal,
  Shield,
  Bug,
  Cloud,
  Network,
  Server,
  Activity,
  BookOpen,
  Settings,
  LogIn,
  LogOut,
  Plus,
  MessageSquare,
  Trash2,
  Building2,
  Globe,
  FileCheck,
  Newspaper,
  ScrollText,
  Menu,
} from "lucide-react";
import ChatMessage from "@/components/ChatMessage";
import ChatInput from "@/components/ChatInput";
import CategoryCard from "@/components/CategoryCard";
import VendorBadge from "@/components/VendorBadge";
import NotificationBell from "@/components/NotificationBell";
import heroImage from "@/assets/netops-hero.jpg";
import { streamChatMessage } from "@/lib/chatService";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useChatSessions } from "@/hooks/useChatSessions";
import { useUserRole } from "@/hooks/useUserRole";
import { useOrg } from "@/hooks/useOrg";
import Landing from "@/pages/Landing";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";

const now = () =>
  new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

const Index = () => {
  const { user, signOut } = useAuth();
  const { isAdmin, isOperator } = useUserRole();
  const { org, needsSetup } = useOrg();
  const navigate = useNavigate();
  const {
    sessions,
    activeSessionId,
    messages,
    setMessages,
    loadSession,
    ensureSession,
    persistMessage,
    updateSessionTitle,
    startNewChat,
    deleteSession,
  } = useChatSessions();

  const [activeVendors, setActiveVendors] = useState<string[]>(["Cisco"]);
  const [allVendors, setAllVendors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isFirstUserMsg = useRef(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (user && needsSetup) {
      navigate("/setup-org");
    }
  }, [user, needsSetup, navigate]);

  useEffect(() => {
    supabase.from("vendors").select("name").order("name").then(({ data }) => {
      if (data) setAllVendors(data.map((v: any) => v.name));
    });
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    isFirstUserMsg.current = !messages.some((m) => m.role === "user");
  }, [activeSessionId]);

  // Show landing page for unauthenticated users
  if (!user) return <Landing />;

  const toggleVendor = (v: string) =>
    setActiveVendors((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    );

  const handleSend = async (text: string) => {
    const userMsg = { role: "user" as const, content: text, timestamp: now() };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    const sessionId = await ensureSession();
    if (sessionId) {
      await persistMessage(sessionId, userMsg);
      if (isFirstUserMsg.current) {
        isFirstUserMsg.current = false;
        updateSessionTitle(sessionId, text);
      }
    }

    let contentSoFar = "";
    let detectedIntents: string[] | undefined;

    setMessages((prev) => [...prev, { role: "assistant" as const, content: "", timestamp: now() }]);

    await streamChatMessage({
      message: text,
      activeVendors,
      onDelta: (chunk) => {
        contentSoFar += chunk;
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "assistant") {
            updated[updated.length - 1] = { ...last, content: contentSoFar, intents: detectedIntents };
          }
          return updated;
        });
      },
      onIntents: (intents) => {
        detectedIntents = intents;
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "assistant") {
            updated[updated.length - 1] = { ...last, intents };
          }
          return updated;
        });
      },
      onDone: async () => {
        setIsLoading(false);
        if (sessionId && contentSoFar) {
          await persistMessage(sessionId, {
            role: "assistant",
            content: contentSoFar,
            timestamp: now(),
            intents: detectedIntents,
          });
        }
      },
      onError: (error) => {
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "assistant") {
            updated[updated.length - 1] = { ...last, content: error || "Sorry, an error occurred." };
          }
          return updated;
        });
        setIsLoading(false);
      },
    });
  };

  const handleCategory = (key: string, prompt: string) => {
    handleSend(prompt);
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo area */}
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <div className="rounded-lg bg-primary/10 p-2 glow-cyan">
          <Shield className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-sm font-bold tracking-tight">NetOps Security</h1>
          {org ? (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Building2 className="h-3 w-3" /> {org.name}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Assistant v1.0</p>
          )}
        </div>
      </div>

      {/* Vendor Filters */}
      <div className="px-5 py-4 border-b border-border">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Vendor Scope
        </h2>
        <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto scrollbar-thin">
          {allVendors.map((v) => (
            <VendorBadge
              key={v}
              name={v}
              active={activeVendors.includes(v)}
              onClick={() => toggleVendor(v)}
            />
          ))}
        </div>
      </div>

      {/* Chat History */}
      {user && sessions.length > 0 && (
        <div className="px-5 py-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Chat History
            </h2>
            <button
              onClick={startNewChat}
              className="p-1 rounded hover:bg-muted/50 transition-colors"
              title="New Chat"
            >
              <Plus className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </div>
          <div className="flex flex-col gap-1 max-h-40 overflow-y-auto scrollbar-thin">
            {sessions.slice(0, 10).map((s) => (
              <div
                key={s.id}
                className={`group flex items-center gap-2 px-2 py-1.5 rounded-md text-xs cursor-pointer transition-colors ${
                  s.id === activeSessionId
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
                onClick={() => { loadSession(s.id); setSidebarOpen(false); }}
              >
                <MessageSquare className="h-3 w-3 flex-shrink-0" />
                <span className="truncate flex-1">{s.title}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                  className="hidden group-hover:block p-0.5 rounded hover:bg-destructive/20"
                >
                  <Trash2 className="h-3 w-3 text-destructive" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="flex-1 overflow-y-auto px-5 py-4 scrollbar-thin">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Quick Actions
        </h2>
        <div className="flex flex-col gap-2">
          <CategoryCard
            icon={Terminal}
            title="CLI Commands"
            description="Look up vendor-specific commands and syntax"
            onClick={() => { handleCategory("cli", "Show me common CLI commands for network verification"); setSidebarOpen(false); }}
          />
          <CategoryCard
            icon={Bug}
            title="Troubleshoot"
            description="Step-by-step diagnostic playbooks"
            onClick={() => { handleCategory("troubleshoot", "Show me a troubleshooting playbook for connectivity loss"); setSidebarOpen(false); }}
          />
          <CategoryCard
            icon={Shield}
            title="CVE Lookup"
            description="Search known vulnerabilities by vendor"
            onClick={() => { handleCategory("cve", "Show me recent critical CVEs for network devices"); setSidebarOpen(false); }}
          />
          <CategoryCard
            icon={Cloud}
            title="Cloud Networking"
            description="AWS, Azure, GCP networking references"
            onClick={() => { handleCategory("cloud", "Show me an AWS VPC networking quick reference"); setSidebarOpen(false); }}
          />
        </div>
      </div>

      {/* Navigation */}
      <div className="px-5 py-3 border-t border-border space-y-1">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          Knowledge Base
        </h2>
        <Link to="/cves" onClick={() => setSidebarOpen(false)}>
          <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
            <Shield className="h-3.5 w-3.5" />
            CVE Browser
          </button>
        </Link>
        <Link to="/ports" onClick={() => setSidebarOpen(false)}>
          <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
            <Globe className="h-3.5 w-3.5" />
            Port Lookup
          </button>
        </Link>
        <Link to="/compliance" onClick={() => setSidebarOpen(false)}>
          <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
            <FileCheck className="h-3.5 w-3.5" />
            Compliance Controls
          </button>
        </Link>
        <Link to="/kb-articles" onClick={() => setSidebarOpen(false)}>
          <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
            <Newspaper className="h-3.5 w-3.5" />
            KB Articles
          </button>
        </Link>

        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 mt-3">
          Navigate
        </h2>
        <Link to="/dashboard" onClick={() => setSidebarOpen(false)}>
          <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
            <Activity className="h-3.5 w-3.5" />
            Dashboard
          </button>
        </Link>
        {isOperator && (
          <Link to="/inventory" onClick={() => setSidebarOpen(false)}>
            <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
              <Server className="h-3.5 w-3.5" />
              Device Inventory
            </button>
          </Link>
        )}
        {isOperator && (
          <Link to="/topology" onClick={() => setSidebarOpen(false)}>
            <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
              <Network className="h-3.5 w-3.5" />
              Network Topology
            </button>
          </Link>
        )}
        {isOperator && (
          <Link to="/sync-history" onClick={() => setSidebarOpen(false)}>
            <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
              <Activity className="h-3.5 w-3.5" />
              Sync History
            </button>
          </Link>
        )}
        {isAdmin && (
          <Link to="/org-settings" onClick={() => setSidebarOpen(false)}>
            <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
              <Building2 className="h-3.5 w-3.5" />
              Org Settings
            </button>
          </Link>
        )}
        {isAdmin && (
          <Link to="/admin" onClick={() => setSidebarOpen(false)}>
            <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
              <Settings className="h-3.5 w-3.5" />
              Admin Dashboard
            </button>
          </Link>
        )}
        {isAdmin && (
          <Link to="/audit-logs" onClick={() => setSidebarOpen(false)}>
            <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
              <ScrollText className="h-3.5 w-3.5" />
              Audit Logs
            </button>
          </Link>
        )}
        {user ? (
          <button
            onClick={() => { signOut(); setSidebarOpen(false); }}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-destructive hover:bg-muted/50 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign Out
          </button>
        ) : (
          <Link to="/auth" onClick={() => setSidebarOpen(false)}>
            <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs text-primary hover:bg-muted/50 transition-colors">
              <LogIn className="h-3.5 w-3.5" />
              Sign In
            </button>
          </Link>
        )}
      </div>

      {/* Stats footer */}
      <div className="border-t border-border px-5 py-3 grid grid-cols-3 gap-2">
        <div className="text-center">
          <Server className="h-3.5 w-3.5 text-muted-foreground mx-auto mb-1" />
          <p className="text-xs font-mono text-primary">142</p>
          <p className="text-[10px] text-muted-foreground">Devices</p>
        </div>
        <div className="text-center">
          <BookOpen className="h-3.5 w-3.5 text-muted-foreground mx-auto mb-1" />
          <p className="text-xs font-mono text-primary">1.2K</p>
          <p className="text-[10px] text-muted-foreground">Commands</p>
        </div>
        <div className="text-center">
          <Activity className="h-3.5 w-3.5 text-muted-foreground mx-auto mb-1" />
          <p className="text-xs font-mono text-primary">98.7%</p>
          <p className="text-[10px] text-muted-foreground">Uptime</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-72 flex-col border-r border-border bg-card">
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar Sheet */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-card">
          {sidebarContent}
        </SheetContent>
      </Sheet>

      {/* Main Chat Area */}
      <main className="flex flex-1 flex-col min-w-0">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-border px-3 sm:px-6 py-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              className="lg:hidden rounded-lg bg-primary/10 p-2"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-4 w-4 text-primary" />
            </button>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold truncate">Network Operations Chat</h2>
              <div className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-glow flex-shrink-0" />
                <span className="text-xs text-muted-foreground truncate">Online — Multi-vendor mode</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <NotificationBell />
            <div className="hidden sm:flex items-center gap-2">
              <Network className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-mono text-muted-foreground">
                {activeVendors.length} vendor{activeVendors.length !== 1 ? "s" : ""} active
              </span>
            </div>
          </div>
        </header>

        {/* Hero Banner (shows when few messages) */}
        {messages.length <= 1 && (
          <div className="relative mx-3 sm:mx-6 mt-4 rounded-lg overflow-hidden h-24 sm:h-32">
            <img
              src={heroImage}
              alt="Network operations visualization"
              className="absolute inset-0 w-full h-full object-cover opacity-40"
            />
            <div className="relative z-10 flex items-center justify-center h-full">
              <div className="text-center px-4">
                <h2 className="text-base sm:text-lg font-bold text-glow">NetOps Security Assistant</h2>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                  Multi-vendor CLI • Troubleshooting • CVE Tracking • Cloud Networking
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 space-y-4 scrollbar-thin">
          {messages.map((msg, i) => (
            <ChatMessage key={i} role={msg.role} content={msg.content} timestamp={msg.timestamp} intents={msg.intents} />
          ))}
          {isLoading && (
            <div className="flex gap-3 animate-slide-up">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center bg-primary/10 glow-border border">
                <Shield className="w-4 h-4 text-primary animate-pulse" />
              </div>
              <div className="bg-card border border-border rounded-lg px-4 py-3">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-border px-3 sm:px-6 py-3 sm:py-4">
          <ChatInput onSend={handleSend} disabled={isLoading} />
          <p className="text-[10px] text-muted-foreground text-center mt-2">
            Powered by AI with data from vendor documentation, IEEE & NIST standards
          </p>
        </div>
      </main>
    </div>
  );
};

export default Index;
